import { useState, useRef, useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { Shield, ArrowRight, Lock } from 'lucide-react'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'

function authErrorMessage(err: unknown, fallback: string): string {
  const e = err as { response?: { status: number; data: unknown } }
  if (!e?.response) {
    return 'Unable to reach the server. Check your connection and try again.'
  }
  const { status, data } = e.response
  const serverMsg = typeof data === 'string' ? data : (data as { message?: string })?.message
  if (status === 429) {
    return serverMsg
      ? `${serverMsg.replace(/\.?$/, '')}. Please wait a few minutes and try again.`
      : 'Too many requests. Please wait a few minutes and try again.'
  }
  if (status === 401 || status === 400) {
    return serverMsg || 'Incorrect email or password.'
  }
  return serverMsg || fallback
}

export function LoginScreen() {
  const { login, verifyMfa, logout } = useAuth()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const [mfaToken, setMfaToken] = useState<string | null>(null)
  const [mfaCode, setMfaCode] = useState('')
  const mfaRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (mfaToken && mfaRef.current) mfaRef.current.focus()
  }, [mfaToken])

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSubmitting(true)
    try {
      const result = await login(email, password)
      if (result && 'mfaRequired' in result && result.mfaRequired) {
        setMfaToken(result.mfaToken as string)
        setSubmitting(false)
        return
      }
      // result is the user — check platform admin
      const user = result as { isPlatformAdmin?: boolean }
      if (!user?.isPlatformAdmin) {
        await logout()
        setError('This portal is for platform administrators.')
        return
      }
      // App.tsx gates on a SEPARATE useAuth() instance that won't observe this
      // hook's state change. Reload so App re-initializes from the now-
      // authenticated session (httpOnly cookie + cached user) and renders the app.
      window.location.reload()
    } catch (err) {
      setError(authErrorMessage(err, 'Login failed'))
    } finally {
      setSubmitting(false)
    }
  }

  const handleMfa = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSubmitting(true)
    try {
      const user = await verifyMfa(mfaToken!, mfaCode)
      if (!user?.isPlatformAdmin) {
        await logout()
        setError('This portal is for platform administrators.')
        return
      }
      // See handleLogin: reload so App's separate useAuth picks up the session.
      window.location.reload()
    } catch (err) {
      setError(authErrorMessage(err, 'Invalid verification code'))
    } finally {
      setSubmitting(false)
    }
  }

  if (mfaToken) {
    return (
      <div className="login-surface flex min-h-screen items-center justify-center">
        <Card className="w-full max-w-md mx-4">
          <CardHeader className="text-center space-y-3 pb-4">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-primary/10 border border-primary/20 mx-auto">
              <Shield className="h-7 w-7 text-primary" />
            </div>
            <h1 className="brand-wordmark text-xl">Two-Factor Authentication</h1>
            <p className="text-sm text-muted-foreground">
              Enter the 6-digit code from your authenticator app
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleMfa} className="space-y-4">
              {error && (
                <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive">
                  {error}
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="mfa-code">Verification Code</Label>
                <Input
                  id="mfa-code"
                  ref={mfaRef}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={8}
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\s/g, ''))}
                  placeholder="000000"
                  className="text-center text-2xl tracking-[0.5em]"
                  required
                />
                <p className="text-xs text-muted-foreground text-center">
                  You can also enter a recovery code
                </p>
              </div>
              <Button
                type="submit"
                className="w-full"
                disabled={submitting || mfaCode.length < 6}
              >
                {submitting ? 'Verifying...' : 'Verify'}
              </Button>
            </form>
            <button
              onClick={() => { setMfaToken(null); setMfaCode(''); setError('') }}
              className="block w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors mt-4"
            >
              Back to login
            </button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="login-surface flex min-h-screen items-center justify-center">
      <Card className="w-full max-w-md mx-4">
        <CardHeader className="text-center pb-4">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-destructive/10 border border-destructive/20 mx-auto mb-3">
            <Shield className="h-7 w-7 text-destructive" />
          </div>
          <h1 className="brand-wordmark text-2xl">DiffDrift Admin</h1>
          <p className="mono-accent text-muted-foreground mt-1 uppercase">
            Platform Administrator Portal
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            {error && (
              <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@company.com"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
              />
            </div>
            <Button
              type="submit"
              className="w-full"
              disabled={submitting}
            >
              {submitting ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                  Authenticating...
                </span>
              ) : (
                <>
                  Sign In
                  <ArrowRight className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
          </form>
          <div className="flex items-center justify-center gap-1.5 text-muted-foreground mt-4">
            <Lock className="h-3 w-3" />
            <span className="mono-accent">Platform Admin Access Only</span>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
